import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../../common/New-ToolTip";
import InfoTool from "../../../../common/InfoTool";
import { drugTypes } from "../../service-steps/clinical-assessment/data";
import { AdherenceTypes } from "../../service-steps/medication/data";
import { hasActiveInterventionLink } from "../../../Patient-Menu/Interventions/data";
import { medicationMatchesAmrEducation } from "../../../../../helpers/amrEducationDrugMatch";

const AMRMedicationList = ({
  filteredMedication,
  reviewedMedicationCards,
  medicationType,
  title,
  isExpanded = true,
  onToggleExpanded,
  expandedMedications = [],
  hoveredMed,
  onMouseEnter,
  onMouseLeave,
  menuMedication,
  onMenuOpen,
  selectedMedication,
  onExpandMedication,
  medicationDetails,
  getNoRecordMessage,
  getCardFromDrug,
  selectMedication,
  isCompleted = false,
}) => {
  const [focusItem, setFocusItem] = useState(null);
  const [showTooltipFor, setShowTooltipFor] = useState(null);
  const tooltipTimeouts = useRef({});
  const expandedMedicationsRef = useRef(expandedMedications);

  useEffect(() => {
    expandedMedicationsRef.current = expandedMedications;
  }, [expandedMedications]);

  // Tooltip only for the truncated name area (not chevron / expand control). Delayed to reduce noise while scrolling.
  const handleMedicationNameMouseEnter = (medId) => {
    if (expandedMedicationsRef.current.includes(medId)) return;
    clearTimeout(tooltipTimeouts.current[medId]);
    tooltipTimeouts.current[medId] = setTimeout(() => {
      if (expandedMedicationsRef.current.includes(medId)) return;
      setShowTooltipFor(medId);
    }, 700);
  };

  const handleMedicationNameMouseLeave = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor((prev) => (prev === medId ? null : prev));
  };

  const handleMedicationNameDoubleClick = (medId) => {
    if (expandedMedicationsRef.current.includes(medId)) return;
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor(medId);
  };

  useEffect(
    () => () => {
      Object.values(tooltipTimeouts.current).forEach(clearTimeout);
    },
    []
  );

  useEffect(() => {
    expandedMedications.forEach((id) => {
      if (tooltipTimeouts.current[id]) {
        clearTimeout(tooltipTimeouts.current[id]);
        delete tooltipTimeouts.current[id];
      }
    });
    setShowTooltipFor((prev) =>
      prev != null && expandedMedications.includes(prev) ? null : prev
    );
  }, [expandedMedications]);

  const isCompactHeader = medicationType === drugTypes.Prescription || medicationType === drugTypes.Additional;
  const amrEducation = useSelector((state) => state.clinicalService.amrEducation || []);
  const interventions = useSelector((state) => state.intervention.interventions || []);
  const currentClinicalServiceId = useSelector(
    (state) => state.clinicalService.clinicalServiceObj?.id
  );
  // Only interventions created inside the current review should light the sidebar
  // icon. The Redux list is patient-wide, so scope it to this clinical service.
  const scopedInterventions = currentClinicalServiceId
    ? interventions.filter(
        (it) => it?.clinicalServiceId === currentClinicalServiceId
      )
    : interventions;
  const hasInterventionLink = (record) =>
    hasActiveInterventionLink(record, scopedInterventions);
  return (
    <>      
      <div
        className={`cursor-pointer rounded-[4px] flex gap-2 ${isCompactHeader ? "h-[46px] py-0" : "py-[10px]"} pr-[10px] border-[1px] border-gray-100 ${ isExpanded ? "border-b-0" : ""} ${ isExpanded ? "bg-gray-50" : "bg-gray-10" } justify-between items-center mt-1`}
        onClick={onToggleExpanded}
      >
          <div className="flex items-center gap-1 flex-1">
          {isCompleted ? (
            <ReactSVG
              src="/icons/checkmark-square-icon.svg"
              className="w-6 h-6 flex-shrink-0"
            />
          ) : (
            <div className="w-6 h-6 flex-shrink-0" />
          )}
          <div className="flex items-center gap-[6px]">
            <p className={`text-gray-800 text-md ${ isExpanded ? "font-semibold" : "" }`}>{title}</p>
            {isExpanded && (
              <>
                <InfoTool id={`amr-${medicationType}-info`} ariaLabel={`${title} info`} />
                <NewToolTip
                  id={`amr-${medicationType}-info`}
                  render={() => (
                    <div className="bg-gray-10 border border-gray-200 p-2 flex flex-col max-w-[300px]">
                      <p className="text-gray-800 text-sm font-medium">
                        {medicationType === drugTypes.Prescription
                          ? "Prescriptions - Medications filled at the current pharmacy."
                          : medicationType === drugTypes.Vaccines
                          ? "Vaccines - Select Vaccine to record immunizations under the Vaccines tab."
                          : "Additional Medications - OTC, samples, study drugs, natural products, or prescriptions filled at alternate pharmacy."}
                      </p>
                    </div>
                  )}
                />
              </>
            )}
          </div>
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 flex-shrink-0 ${isExpanded ? "-rotate-180" : ""}`}
        />
      </div>
      {isExpanded && (
        <>
          <ul
            className="divide-y divide-gray-100 border-y border-y-gray-100 overflow-y-auto h-full w-full max-h-[600px] pt-3"
            onMouseLeave={() => {
              onMouseLeave?.();
            }}
          >
      {(filteredMedication.filter(
        (x) => x.medicationType === medicationType
      ).length > 0 ||
        reviewedMedicationCards?.filter(
          (x) =>
            x.consumption !== AdherenceTypes.Stopped &&
            x.intervention?.override === "stopped"
        ).length > 0) &&
        //filter records, if it is active medication and should not be reviewed as stopped taking.
        filteredMedication
          ?.filter(
            (x) =>
              x.medicationType === medicationType &&
              (reviewedMedicationCards.length === 0 ||
                !reviewedMedicationCards
                  ?.filter(
                    (f) =>
                      f?.consumption === AdherenceTypes.Stopped &&
                      f?.intervention?.override === "stopped"
                  )
                  .map((i) => i.name)
                  .includes(x.displayName))
          )
          .map((med, i) => (
            <li
              key={med.id}
              className="cursor-pointer text-sm text-gray-800 w-full list-none"
              onClick={(e) => {
                setFocusItem(med.id);
                if (med.status === "active") {
                  selectMedication(med);
                }
                e.currentTarget?.scrollIntoView?.({
                  block: "nearest",
                  behavior: "smooth",
                });
              }}
            >
              {(() => {
                const matchesAmrEducation = medicationMatchesAmrEducation(
                  amrEducation,
                  med
                );
                const isRowSelected =
                  selectedMedication?.id === med.id ||
                  focusItem === med.id ||
                  matchesAmrEducation;
                return (
                  <>
                    <div
                      className={`w-full ${
                        isRowSelected
                          ? "bg-blue-100 hover:bg-blue-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div
                        className={`pr-[10px] flex w-full items-center py-[10px] ${
                          med.status === "inactive" ? "bg-red-50" : ""
                        }`}
                      >
                        {/* Tooltip anchor: row body except expand/collapse chevron */}
                        <div
                          data-medication-tooltip={`${medicationType}-${med.id}`}
                          className="flex min-w-0 flex-1 items-center gap-3 pr-3"
                          onMouseEnter={() => {
                            handleMedicationNameMouseEnter(med.id);
                            onMouseEnter(med.id);
                          }}
                          onMouseLeave={() => {
                            handleMedicationNameMouseLeave(med.id);
                            onMouseLeave && onMouseLeave();
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            handleMedicationNameDoubleClick(med.id);
                          }}
                        >
                          <div className="flex w-6 flex-shrink-0 items-center justify-center">
                            {getCardFromDrug(med)?.status === "reviewed" ? (
                              hasInterventionLink(getCardFromDrug(med)) ? (
                                <ReactSVG
                                  src="/icons/intervention-sm.svg"
                                  className="flex h-4 w-4 text-black"
                                />
                              ) : (
                                <ReactSVG
                                  src="/icons/correct-tick-sm2.svg"
                                  className="flex h-4 w-4 text-black"
                                />
                              )
                            ) : getCardFromDrug(med)?.status === "skipped" ? (
                              <ReactSVG
                                src="/icons/skip-sm.svg"
                                className="flex h-4 w-4 text-black"
                              />
                            ) : (
                              <span className="h-4 w-4" aria-hidden />
                            )}
                          </div>
                          <div className="min-w-0 flex-1 overflow-hidden text-left">
                            <span className="flex min-w-0 items-center gap-[6px] px-[2px]">
                              <span className="block truncate text-left text-gray-800">
                                {med.displayName}
                              </span>
                            </span>
                          </div>
                          {((hoveredMed === med.id &&
                            getCardFromDrug(med)?.status !== "reviewed") ||
                            menuMedication?.id === med.id) && (
                            <Menu as="div" className="relative inline-block flex-shrink-0">
                              <Menu.Button
                                onClick={(e) => {
                                  if (
                                    !reviewedMedicationCards
                                      .map((i) => i.name)
                                      .includes(med.displayName)
                                  ) {
                                    onMenuOpen(e, med);
                                  }
                                }}
                              >
                                <ReactSVG
                                  src="/icons/triple-dot.svg"
                                  className={`flex !items-center rounded-md py-2 px-3 ${
                                    med.id === menuMedication?.id && "bg-gray-300"
                                  }`}
                                />
                              </Menu.Button>
                            </Menu>
                          )}
                        </div>
                        <button
                          type="button"
                          className="flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            onExpandMedication(med.id);
                          }}
                        >
                          {!expandedMedications.includes(med.id) && (
                            <ChevronDownIcon
                              className="h-4 w-4"
                              strokeWidth={2}
                            />
                          )}
                          {expandedMedications.includes(med.id) && (
                            <ChevronUpIcon className="h-4 w-4" strokeWidth={2} />
                          )}
                        </button>
                      </div>
                    </div>
                    {expandedMedications.includes(med.id) &&
                      medicationDetails(med)}
                    <NewToolTip
                      id={`amr-med-name-${medicationType}-${med.id}`}
                      anchorSelect={`[data-medication-tooltip="${medicationType}-${med.id}"]`}
                      place="bottom-start"
                      isOpen={
                        showTooltipFor === med.id &&
                        !expandedMedications.includes(med.id)
                      }
                      render={() => (
                        <div className="bg-teal-25 p-2 max-w-md flex flex-col">
                          <p className="text-teal-300 text-left text-body-sm font-normal">
                            {med.displayName}
                          </p>
                          {((med?.manufacturerStoredType === "Small" &&
                            med?.manufacturerCode) ||
                            (med?.manufacturerStoredType === "Full" &&
                              med?.manufacturerName)) && (
                            <p className="text-teal-300 text-left text-body-sm font-normal">
                              {med?.manufacturerStoredType === "Small" &&
                              med?.manufacturerCode
                                ? ` (${med?.manufacturerCode})`
                                : med?.manufacturerStoredType === "Full" &&
                                    med?.manufacturerName
                                  ? ` (${med?.manufacturerName})`
                                  : ""}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </>
                );
              })()}
            </li>
          ))}
          </ul>
        </>
      )}
    </>
  );
};

export default AMRMedicationList;
